import moment from 'moment-timezone';
import React, { forwardRef, useCallback, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import styled from 'styled-components';

import '../../assets/stylesheets/molecules/datepicker.scss';
import { useFleet } from '../../hooks';
import RarrowIcon from '../../assets/images/icons/rarrow.svg';

const MONTH_FORMAT = 'MMMM yyyy';

interface MonthRangePickerProps {
  startDate: Date;
  endDate: Date;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  /** Maximum selectable range in months (inclusive). Defaults to unlimited. */
  maxMonths?: number;
}

const PickerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .react-datepicker__navigation {
    top: 6px;
  }
`;

const DateButtonStyled = styled.button`
  background: #f8f9fa;
  border: 1px solid #e4e9fb;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #e9ecef;
  }

  &:focus {
    outline: none;
    border-color: #3366cc;
  }
`;

interface DateButtonProps {
  value?: string;
  onClick?: () => void;
}

const DateButton = forwardRef<HTMLButtonElement, DateButtonProps>(({ value, onClick }, ref) => (
  <DateButtonStyled type="button" onClick={onClick} ref={ref}>
    {value}
  </DateButtonStyled>
));

DateButton.displayName = 'DateButton';

/**
 * A month-only date range picker component.
 * Displays dates in "December 2026" format and allows selecting month ranges.
 */
export const MonthRangePicker: React.FC<MonthRangePickerProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  maxMonths,
}) => {
  const fleet = useFleet();
  const fleetCreatedAt = useMemo(() => new Date(fleet.created_at || Date.now()), [fleet.created_at]);
  // Current month is disabled — the last selectable month is always the previous complete month
  const maxDate = useMemo(() => moment().subtract(1, 'month').endOf('month').toDate(), []);

  // Start picker: lower-bounded by fleet creation; upper-bounded by endDate
  // If maxMonths is set, also lower-bounded so the range from endDate doesn't exceed limit
  const startMinDate = useMemo(() => {
    if (maxMonths == null) return fleetCreatedAt;
    const limitedMin = moment(endDate)
      .subtract(maxMonths - 1, 'months')
      .startOf('month')
      .toDate();
    return limitedMin > fleetCreatedAt ? limitedMin : fleetCreatedAt;
  }, [fleetCreatedAt, endDate, maxMonths]);

  // End picker: lower-bounded by startDate; upper-bounded by last complete month
  // If maxMonths is set, also upper-bounded so the range from startDate doesn't exceed limit
  const endMaxDate = useMemo(() => {
    if (maxMonths == null) return maxDate;
    const limitedMax = moment(startDate)
      .add(maxMonths - 1, 'months')
      .endOf('month')
      .toDate();
    return limitedMax < maxDate ? limitedMax : maxDate;
  }, [startDate, maxMonths, maxDate]);

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      if (!date) return;
      // Set to start of month
      const startOfMonth = moment(date).startOf('month').toDate();
      onStartDateChange(startOfMonth);
    },
    [onStartDateChange],
  );

  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      if (!date) return;
      // Set to end of month
      const endOfMonth = moment(date).endOf('month').toDate();
      onEndDateChange(endOfMonth);
    },
    [onEndDateChange],
  );

  return (
    <PickerContainer>
      <DatePicker
        selected={startDate}
        onChange={handleStartDateChange}
        showMonthYearPicker
        dateFormat={MONTH_FORMAT}
        minDate={startMinDate}
        maxDate={endDate}
        customInput={<DateButton />}
        popperPlacement="bottom-end"
      />

      <img src={RarrowIcon} alt="" aria-hidden="true" style={{ width: '16px', height: '24px' }} />

      <DatePicker
        selected={endDate}
        onChange={handleEndDateChange}
        showMonthYearPicker
        dateFormat={MONTH_FORMAT}
        minDate={startDate}
        maxDate={endMaxDate}
        customInput={<DateButton />}
        popperPlacement="bottom-end"
      />
    </PickerContainer>
  );
};
